import { cn } from "@/app/utils/cn";
import { Checkbox } from "@/view/components/ui/checkbox";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";

interface Option {
  value: string;
  label: string;
  subOptions?: Option[];
  commission?: number;
}

interface SelectTableProps {
  options: Option[];
  initialValue?: string[] | string;
  setSelectedTables: (tables: string[]) => void;
  selectedTables: string[];
}

export const SelectTables = ({
  options,
  initialValue,
  setSelectedTables: setValue,
  selectedTables: value,
}: SelectTableProps) => {
  const [openSubOptions, setOpenSubOptions] = useState<Record<string, boolean>>(
    {},
  );

  const handleSelect = (option: Option, checked: boolean, parent?: Option) => {
    if (checked) {
      if (parent?.value?.toUpperCase() === "FGTS" && parent.subOptions) {
        const siblingValues = parent.subOptions.map((s) => s.value);
        const withoutSiblings = value.filter((v) => !siblingValues.includes(v));

        setValue([...withoutSiblings, option.value]);
        return;
      }

      setValue([...value, option.value]);
    } else {
      setValue(value.filter((v) => v !== option.value));
    }
  };

  useEffect(() => {
    const toOpen: Record<string, boolean> = {};

    for (const option of options) {
      if (option.subOptions?.some((sub) => initialValue?.includes(sub.value))) {
        toOpen[option.value] = true;
      }
    }

    if (Object.keys(toOpen).length > 0) {
      setOpenSubOptions((prev) => ({ ...prev, ...toOpen }));
    }
  }, [options, initialValue]);

  const hasSomeChecked = (subOptions: Option[]) =>
    subOptions.some((sub) => value.includes(sub.value));

  return (
    <>
      <h1>Selecione as tabelas</h1>

      <ul className="flex flex-col gap-2">
        {options.map((option) => {
          const hasSubOptions = !!option.subOptions?.length;

          const checked = hasSubOptions
            ? hasSomeChecked(option.subOptions!)
            : value.includes(option.value);

          return (
            <li
              key={option.value}
              className={cn(
                "flex flex-col rounded-lg border border-gray-200",
                checked && "border-primary",
              )}
            >
              {hasSubOptions && (
                <div
                  className="flex cursor-pointer items-center justify-between gap-2 p-3"
                  onClick={() =>
                    setOpenSubOptions((prev) => ({
                      ...prev,
                      [option.value]: !prev[option.value],
                    }))
                  }
                >
                  <label className="text-sm font-medium">{option.label}</label>

                  {openSubOptions[option.value] ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              )}

              {!hasSubOptions && (
                <div className="flex items-center gap-2 p-3">
                  <Item
                    option={option}
                    checked={checked}
                    onCheckedChange={(opt, checked) =>
                      handleSelect(opt, checked)
                    }
                  />
                </div>
              )}

              {hasSubOptions && openSubOptions[option.value] && (
                <ul className="mb-2 flex flex-col">
                  {option.subOptions!.map((subOption) => {
                    const subChecked = value.includes(subOption.value);

                    return (
                      <li
                        key={subOption.value}
                        className="flex items-center gap-2 p-3 hover:bg-gray-100"
                      >
                        <Item
                          option={subOption}
                          checked={subChecked}
                          onCheckedChange={(opt, checked) =>
                            handleSelect(opt, checked, option)
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
};

const Item = ({
  option,
  checked,
  onCheckedChange,
}: {
  option: Option;
  checked: boolean;
  onCheckedChange: (option: Option, checked: boolean) => void;
}) => {
  return (
    <>
      <Checkbox
        checked={checked}
        className="data-[state=checked]:border-primary"
        onCheckedChange={(checked: boolean) => onCheckedChange(option, checked)}
      />

      <label className="flex w-full items-center justify-between text-sm font-medium">
        {option.label}

        {option.commission !== undefined && (
          <span className="text-sm font-medium text-gray-500">
            {option.commission}%
          </span>
        )}
      </label>
    </>
  );
};
